import React, { useState, useEffect, useCallback } from 'react';
import { 
  useTranslationKeys, 
  useAvailableLocales, 
  useKeyTypes, 
  useUpdateOrCreateTranslationKey,
  useCreateTranslationKey,
  useUpdateTranslationKey,
  useLocales,
  useGetTranslationKeysCsvFile,
  useImportTranslationKeysCsvFile
} from '../../../hooks/useLanguages';
import CustomPaginate from '../../../components/PaginateComponent';
import LabelEditModal from './LabelEditModal';
import { useTranslation } from '../../../hooks/useTranslation';
import { Tooltip } from 'react-tooltip';
import './LanguageLabalesTab.scss';
import { toast } from 'react-toastify';

const LanguageLabalesTab = () => {
  const {t} = useTranslation();
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLabel, setEditingLabel] = useState(null);
  const [formData, setFormData] = useState({
    key: '',
    key_type_id: 1,
    translations: []
  });

  const [pageSize] = useState(20);
  
  // Filter states
  const [firstLocale, setFirstLocale] = useState('');
  const [secondLocale, setSecondLocale] = useState('');
  const [typeId, setTypeId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Query parameters for API call
  const queryParams = {
    page: currentPage,
    limit: pageSize,
    first_locale: firstLocale,
    second_locale: secondLocale || '', // Always include second_locale, empty string if not selected
    ...(typeId && { type_id: typeId }),
    ...(searchTerm && { search: searchTerm })
  };
  
  const { data: translationKeysCsvFile } = useGetTranslationKeysCsvFile(
    secondLocale ? queryParams : {},
    { enabled: !!secondLocale }
  );
  const { mutate: importTranslationKeysCsvFile } = useImportTranslationKeysCsvFile({
    onSuccess: (data) => {
      toast.success(data?.messages || t('languageLabels.messages.csvUploadedSuccess'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || t('languageLabels.messages.anErrorOccurred'));
    },
  });
  const { data: translationKeys, isLoading, error } = useTranslationKeys(queryParams);
  const { data: keyTypes } = useKeyTypes();
  const { data: availableLocales } = useAvailableLocales({ enabled: showAddModal });
  const { data: locales } = useLocales({});
  
  // Enhanced mutation handling
  const { mutate: updateOrCreateTranslationKey } = useUpdateOrCreateTranslationKey({
    onSuccess: () => {
      toast.success(t('languageLabels.messages.labelUpdatedSuccess'));
      setShowAddModal(false);
      setEditingLabel(null);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || t('languageLabels.messages.anErrorOccurred'));
    },
  });

  const { mutate: createTranslationKey } = useCreateTranslationKey({
    onSuccess: () => {
      toast.success(t('languageLabels.messages.labelCreatedSuccess'));
      setShowAddModal(false);
      setEditingLabel(null);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || t('languageLabels.messages.anErrorOccurred'));
    },
  });

  const { mutate: updateTranslationKey } = useUpdateTranslationKey({
    onSuccess: () => {
      toast.success(t('languageLabels.messages.labelUpdatedSuccess'));
      setShowAddModal(false);
      setEditingLabel(null);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || t('languageLabels.messages.anErrorOccurred'));
    },
  });
  const keyTypesOptions = keyTypes?.map(keyType => ({
    value: keyType.id,
    label: typeof keyType.name === 'string' ? keyType.name : (keyType.name?.name || keyType.name || '')
  }));

  // Reset form function
  const resetForm = () => {
    setFormData({
      key: '',
      key_type_id: 1,
      translations: []
    });
  };

  const firstLocalesOptions = locales?.map(key => {
    // Handle case where name or code might be objects
    const name = typeof key.name === 'string' ? key.name : (key.name?.name || key.name || '');
    const code = typeof key.code === 'string' ? key.code : (key.code?.code || key.code || '');
    return {
      value: key.id,
      label: `${name} (${code})`
    };
  });

  const secondLocalesOptions = locales?.map(key => {
    // Handle case where name or code might be objects
    const name = typeof key.name === 'string' ? key.name : (key.name?.name || key.name || '');
    const code = typeof key.code === 'string' ? key.code : (key.code?.code || key.code || '');
    return {
      value: key.id,
      label: `${name} (${code})`
    };
  });
  
  // Pagination data from API response
  const paginationData = {
    data: translationKeys?.data || [],
    limit: translationKeys?.limit || 20,
    page: translationKeys?.page || 1,
    pages: translationKeys?.pages || 1,
    total: translationKeys?.total || 1
  };
  
  const [labels, setLabels] = useState([]);

  // Update labels when translationKeys data changes
  useEffect(() => {
    if (translationKeys?.data) {
      setLabels(translationKeys.data);
    }
  }, [translationKeys]);

  // Set first locale to first available option by default
  useEffect(() => {
    if (firstLocalesOptions && firstLocalesOptions.length > 0 && !firstLocale) {
      setFirstLocale(firstLocalesOptions[0].value);
    }
  }, [firstLocalesOptions]);


  // No client-side filtering needed since we're using server-side filtering
  const filteredLabels = labels;

  const handleAddLabel = () => {
    setEditingLabel(null);
    resetForm();
    setShowAddModal(true);
  };

  const handleEditLabel = (label) => {
    setEditingLabel(label);
    setFormData({
      key: label.key,
      key_type_id: label.key_type_id || 1,
      translations: label.translations || []
    });
    setShowAddModal(true);
  };

  const handleSubmit = () => {
    
    // Prepare the data for submission
    const submitData = {
      ...formData,
      // Ensure translations array is properly formatted
      translations: formData.translations.filter(t => t.label && t.label.trim() !== '')
    };

    if (editingLabel) {
      // Update existing translation key
      updateTranslationKey({
        id: editingLabel.id,
        ...submitData
      });
    } else {
      // Create new translation key
      createTranslationKey(submitData);
    }
  };

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleTranslationChange = useCallback((localeId, value) => {
    setFormData(prev => {
      const existingTranslationIndex = prev.translations.findIndex(t => t.locale_id === localeId);
      const newTranslations = [...prev.translations];
      
      if (existingTranslationIndex >= 0) {
        newTranslations[existingTranslationIndex] = {
          ...newTranslations[existingTranslationIndex],
          label: value
        };
      } else {
        newTranslations.push({
          label: value,
          locale_id: localeId
        });
      }
      
      return {
        ...prev,
        translations: newTranslations
      };
    });
  }, []);

  const getTranslationValue = useCallback((localeId) => {
    // Handle both array format and object format for translations
    if (Array.isArray(formData.translations)) {
      const translation = formData.translations.find(t => t.locale_id === localeId);
      return translation ? translation.label : '';
    }
    
    // Handle object format where translations might be keyed by locale_id
    if (formData.translations && typeof formData.translations === 'object') {
      return formData.translations[localeId]?.label || '';
    }
    
    return '';
  }, [formData.translations]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('languageLabels.copiedToClipboard'));
    // You could add a toast notification here
  };

  const handlePageClick = (event) => {
    const selectedPage = event.selected + 1;
    setCurrentPage(selectedPage);
  };

  const handleFilterChange = (filterType, value) => {
    setCurrentPage(1); // Reset to first page when filters change
    
    switch (filterType) {
      case 'firstLocale':
        setFirstLocale(value);
        break;
      case 'secondLocale':
        setSecondLocale(value);
        break;
      case 'typeId':
        setTypeId(value);
        break;
      case 'search':
        setSearchTerm(value);
        break;
      default:
        break;
    }
  };

  const clearFilter = (filterType) => {
    setCurrentPage(1);
    
    switch (filterType) {
      case 'firstLocale':
        setFirstLocale(firstLocalesOptions?.[0]?.value || '');
        break;
      case 'secondLocale':
        setSecondLocale('');
        break;
      case 'typeId':
        setTypeId('');
        break;
      case 'search':
        setSearchTerm('');
        break;
      default:
        break;
    }
  };

  // CSV Download functionality
  const handleDownloadCsv = () => {
    if (translationKeysCsvFile) {
      const blob = new Blob([translationKeysCsvFile], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `translation-keys-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t('languageLabels.messages.csvDownloadedSuccess'));
    }
  };

  // CSV Upload functionality
  const handleUploadCsv = (event) => {
    const file = event.target.files[0];
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    
    if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a valid CSV file');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    // Add any additional required fields if needed
    if (secondLocale) {
      formData.append('locale_id', secondLocale);
    }
    
    importTranslationKeysCsvFile(formData);
    
    // Reset the input value so the same file can be selected again
    event.target.value = '';
  };


  return (
    <div className="language-management__content">
      {/* Header with Filters and Add Button */}
      <div className="language-management__labels-header">
        <div className="language-management__action-buttons">
          <button 
            className="language-management__csv-btn download"
            onClick={handleDownloadCsv}
            disabled={!translationKeysCsvFile || !secondLocale}
            data-tooltip-id="download-csv-tooltip"
            data-tooltip-content={!secondLocale ? t('languageLabels.selectSecondLocaleToDownload') : ''}
          >
            📥 {t('languageLabels.downloadCsv')}
          </button>
          <Tooltip 
            id="download-csv-tooltip" 
            place="top-end"
            disabled={!!secondLocale}
          />
          <label className="language-management__csv-btn upload">
            📤 {t('languageLabels.uploadCsv')}
            <input
              type="file"
              accept=".csv"
              onChange={handleUploadCsv}
              style={{ display: 'none' }}
            />
          </label>
          {/* <button 
            className="language-management__add-btn"
            onClick={handleAddLabel}
          >
            {t('languageLabels.addNewLabel')}
          </button> */}
        </div>
        <div className="language-management__labels-controls">
          <div className="language-management__filter-group">
            <label>{t('languageLabels.search')}</label>
            <div className="language-management__filter-input-wrapper">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder={t('languageLabels.searchPlaceholder')}
                className="language-management__filter-input"
              />
              {searchTerm && (
                <button
                  className="language-management__clear-filter-btn"
                  onClick={() => clearFilter('search')}
                  title={t('languageLabels.clearSearch')}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          <div className="language-management__filter-group">
            <label>{t('languageLabels.firstLocale')}</label>
            <div className="language-management__filter-input-wrapper">
              <select
                value={firstLocale}
                onChange={(e) => handleFilterChange('firstLocale', e.target.value)}
                className="language-management__filter-select"
              >
                <option value="">{t('languageLabels.selectFirstLocale')}</option>
                {firstLocalesOptions?.map((locale, index) => (
                  <option key={index} value={locale.value}>
                    {locale.label}
                  </option>
                ))}
              </select>
              {firstLocale && (
                <button
                  className="language-management__clear-filter-btn"
                  onClick={() => clearFilter('firstLocale')}
                  title={t('languageLabels.clearFirstLocale')}
                >
                  ×
                </button>
              )}
            </div>
          </div>
          
          <div className="language-management__filter-group">
            <label>{t('languageLabels.secondLocale')}</label>
            <div className="language-management__filter-input-wrapper">
              <select
                value={secondLocale}
                onChange={(e) => handleFilterChange('secondLocale', e.target.value)}
                className="language-management__filter-select"
              >
                <option value="">{t('languageLabels.allSecondLocales')}</option>
                {secondLocalesOptions?.map((locale, index) => (
                  <option key={index} value={locale.value}>
                    {locale.label}
                  </option>
                ))}
              </select>
              {secondLocale && (
                <button
                  className="language-management__clear-filter-btn"
                  onClick={() => clearFilter('secondLocale')}
                  title={t('languageLabels.clearSecondLocale')}
                >
                  ×
                </button>
              )}
            </div>
          </div>
          
          <div className="language-management__filter-group">
            <label>{t('languageLabels.type')}</label>
            <div className="language-management__filter-input-wrapper">
              <select
                value={typeId}
                onChange={(e) => handleFilterChange('typeId', e.target.value)}
                className="language-management__filter-select"
              >
                <option value="">{t('languageLabels.allTypes')}</option>
                {keyTypesOptions?.map((category, index) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
              {typeId && (
                <button
                  className="language-management__clear-filter-btn"
                  onClick={() => clearFilter('typeId')}
                  title={t('languageLabels.clearType')}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Labels Table */}
      <div className="language-management__labels-table">
        <div className="language-management__table-header">
          <div className="language-management__table-cell" data-label="Label Key">{t('languageLabels.tableHeaders.labelKey')}</div>
          <div className="language-management__table-cell" data-label="First Translation">{t('languageLabels.tableHeaders.firstTranslation')}</div>
          <div className="language-management__table-cell" data-label="Second Translation">{t('languageLabels.tableHeaders.secondTranslation')}</div>
          <div className="language-management__table-cell" data-label="Type">{t('languageLabels.tableHeaders.type')}</div>
          <div className="language-management__table-cell" data-label="Created">{t('languageLabels.tableHeaders.created')}</div>
          <div className="language-management__table-cell" data-label="Actions">{t('languageLabels.tableHeaders.actions')}</div>
        </div>
        
        {isLoading ? (
          <div className="language-management__loading">
            <div className="language-management__loading-spinner"></div>
            <p>{t('languageLabels.loading')}</p>
          </div>
        ) : error ? (
          <div className="language-management__error">
            <p>{t('languageLabels.error')}</p>
          </div>
        ) : filteredLabels.length === 0 ? (
          <div className="language-management__empty">
            <p>{t('languageLabels.noData')}</p>
          </div>
        ) : (
          filteredLabels.map((label) => (
          <div key={label.id} className="language-management__table-row">
            <div className="language-management__table-cell" data-label="Label Key">
              <div className="language-management__label-key">
                <code>{label.key}</code>
                <button
                  className="language-management__copy-btn"
                  onClick={() => copyToClipboard(label.key)}
                  title={t('languageLabels.copyKey')}
                >
                  📋
                </button>
              </div>
            </div>
            <div className="language-management__table-cell" data-label="First Translation">
              <div className="language-management__translation">
                  {typeof label.first_translation?.label === 'string' ? label.first_translation?.label : t('languageLabels.notSet')}
                {typeof label.first_translation?.label === 'string' && label.first_translation?.label && (
                  <button
                    className="language-management__copy-btn"
                    onClick={() => copyToClipboard(label.first_translation?.label)}
                    title={t('languageLabels.copyFirstTranslation')}
                  >
                    📋
                  </button>
                )}
              </div>
            </div>
            <div className="language-management__table-cell" data-label="Second Translation">
              <div className="language-management__translation">
                {typeof label.second_translation?.label === 'string' ? label.second_translation?.label : t('languageLabels.notSet')}
                {typeof label.second_translation?.label === 'string' && label.second_translation?.label && (
                  <button
                    className="language-management__copy-btn"
                    onClick={() => copyToClipboard(label.second_translation?.label)}
                    title={t('languageLabels.copySecondTranslation')}
                  >
                    📋
                  </button>
                )}
              </div>
            </div>
            <div className="language-management__table-cell" data-label="Type">
              <span className="language-management__category-badge">
                {typeof label.type?.name === 'string' ? label.type.name : t('languageLabels.unknown')}
              </span>
            </div>
            <div className="language-management__table-cell" data-label="Created">
              <span className="language-management__date">
                {new Date(label.created_at).toLocaleDateString()}
              </span>
            </div>
            <div className="language-management__table-cell" data-label="Actions">
              <div className="language-management__actions">
                <button
                  className="language-management__action-btn edit"
                  onClick={() => handleEditLabel(label)}
                  title={t('languageLabels.editLabel')}
                >
                  ✏️
                </button>
              </div>
            </div>
          </div>
        ))
        )}
      </div>

      {/* Pagination */}
      {paginationData.pages > 1 && (
        <div className="language-management__pagination">
          <CustomPaginate
            pageCount={paginationData.pages}
            handlePageClick={handlePageClick}
            currentPage={currentPage}
          />
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <LabelEditModal
          showModal={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setEditingLabel(null);
            resetForm();
          }}
          onSubmit={handleSubmit}
          editingLabel={editingLabel}
          formData={formData}
          onInputChange={handleInputChange}
          onTranslationChange={handleTranslationChange}
          getTranslationValue={getTranslationValue}
          keyTypesOptions={keyTypesOptions}
        />
      )}
    </div>
  );
};

export default LanguageLabalesTab;